import {
  useGetDemoQuery,
  useGetRevisionsQuery,
  useUpdateDemoMutation,
} from '@carboncopyco/data';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  InlineLink,
  LoadingDots,
  TextBase,
  TextCaption,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { MMMDoYYYY, dateUTCFromNow } from '@carboncopyco/utils';
import { usePermissionsContext } from '../../../utils';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { InfoPageEditableDescription } from '../../InfoPageEdiableDescription';
import { useOverlayPageProps } from '../OverlayPageContext';

export const DetailsCard = () => {
  const { organizationId, demoId, workspaceId, readOnly } =
    useOverlayPageProps();
  const { getPermissions } = usePermissionsContext();
  const [updateDemo] = useUpdateDemoMutation();
  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const { data } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'demo',
    id: demoId,
    reverseOrder: true,
  });
  const { description, creator, firstUrl, createTime = '' } = demo || {};
  const revisions = data?.items;
  const latestVersion = revisions?.[0];
  const defaultDescription = `Created by ${creator?.firstName} ${
    creator?.lastName
  }, on ${MMMDoYYYY(createTime)}`;

  const updateDemoPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.updateDemoById
  );

  return (
    <TitleCard
      header={
        <div className="flex justify-between items-center px-6 h-ds-15">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Details"
          />
        </div>
      }
      radius
    >
      {demo && revisions ? (
        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col gap-1">
            <TextCaption label="Description" />
            <div className="max-w-ds-96">
              <InfoPageEditableDescription
                description={description || defaultDescription}
                disabled={readOnly || !updateDemoPermission}
                onDescriptionChange={(value) =>
                  value
                    ? updateDemo({
                        organizationId,
                        workspaceId,
                        id: demoId,
                        description: value,
                      })
                    : undefined
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 truncate">
            <TextCaption label="Source" />
            <div className="max-w-ds-96 truncate hover:text-ds-primary">
              <div className="flex items-end gap-1">
                <TextBase
                  color="text-ds-natural-600"
                  label="URL:"
                />
                <InlineLink
                  href={firstUrl}
                  target="_blank"
                  label={firstUrl}
                  truncateWidth={340}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <TextCaption label="Version" />

            {latestVersion && (
              <div className="flex items-center gap-1">
                <TextBase
                  color="text-ds-natural-600"
                  label={`Version ${latestVersion.currRevision + 1}`}
                />
                <TextCaption
                  label={`(Edited ${dateUTCFromNow(
                    latestVersion.createTime
                  )}, by ${latestVersion.creator.name})`}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-56">
          <LoadingDots />
        </div>
      )}
    </TitleCard>
  );
};
